/**
 * Utilitaires pour calculer le score de fiabilité basé sur la cohérence des dates
 * entre les expériences extraites du CV et les déclarations des référents
 * CORRIGÉ : Utilise maintenant la nouvelle logique de cohérence temporelle
 */

import { validateTemporalCoherence, analyzeCampaignTemporalCoherence } from './temporalCoherence';

interface CVExperience {
    id: string;
    startDate: string; // Format YYYY-MM
    endDate: string | null; // null si en cours
    company: string;
    position: string;
}

interface ReferentDates {
    collaboration_start_date: string;
    collaboration_end_date: string | null;
}

interface DateReliabilityResult {
    score: number; // 0-10
    issues: string[];
    severity: 'low' | 'medium' | 'high';
    details: {
        startDateGap: number; // en mois
        endDateGap: number; // en mois
        hasTemporalOverlap: boolean;
        durationDifference: number; // en mois
    };
}

/**
 * Calcule l'écart en mois entre deux dates au format YYYY-MM
 */
function calculateMonthGap(date1: string | null, date2: string | null): number {
    if (!date1 || !date2) return 0;

    const [year1, month1] = date1.split('-').map(Number);
    const [year2, month2] = date2.split('-').map(Number);

    return Math.abs((year2 - year1) * 12 + (month2 - month1));
}

/**
 * Convertit une date string en format YYYY-MM vers un objet Date
 */
function parseYearMonth(dateStr: string): Date {
    const [year, month] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, 1); // month - 1 car les mois JS commencent à 0
}

/**
 * Vérifie s'il y a un chevauchement temporel entre les périodes CV et référent
 */
function hasTemporalOverlap(cvExperience: CVExperience, referentDates: ReferentDates): boolean {
    try {
        const cvStart = parseYearMonth(cvExperience.startDate);
        const cvEnd = cvExperience.endDate ? parseYearMonth(cvExperience.endDate) : new Date(); // Si en cours, utiliser maintenant

        // Parser les dates du référent (format peut varier)
        let refStart: Date;
        let refEnd: Date;

        // Essayer de parser la date de début du référent
        if (referentDates.collaboration_start_date.includes('-')) {
            // Format YYYY-MM-DD ou YYYY-MM
            const refStartStr = referentDates.collaboration_start_date.substring(0, 7); // Prendre YYYY-MM
            refStart = parseYearMonth(refStartStr);
        } else {
            return false; // Format non reconnu
        }

        // Essayer de parser la date de fin du référent
        if (referentDates.collaboration_end_date) {
            if (referentDates.collaboration_end_date.includes('-')) {
                const refEndStr = referentDates.collaboration_end_date.substring(0, 7);
                refEnd = parseYearMonth(refEndStr);
            } else {
                refEnd = new Date(); // Si pas de date de fin, considérer comme en cours
            }
        } else {
            refEnd = new Date(); // Si null, considérer comme en cours
        }

        // Vérifier le chevauchement : les périodes se chevauchent si start1 <= end2 && start2 <= end1
        return cvStart <= refEnd && refStart <= cvEnd;

    } catch (_error) {
        console.warn('Erreur lors du parsing des dates:', error);
        return false;
    }
}

/**
 * Calcule la durée en mois d'une expérience
 */
function calculateDurationMonths(startDate: string, endDate: string | null): number {
    const start = parseYearMonth(startDate);
    const end = endDate ? parseYearMonth(endDate) : new Date();

    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    return (endYear - startYear) * 12 + (endMonth - startMonth);
}

/**
 * Fonction principale pour calculer le score de fiabilité des dates (CORRIGÉE)
 * Utilise maintenant la nouvelle logique de cohérence temporelle
 */
export function calculateDateReliabilityScore(
    cvExperience: CVExperience,
    referentDates: ReferentDates
): DateReliabilityResult {
    // Utiliser la nouvelle logique de validation temporelle
    const temporalResult = validateTemporalCoherence(cvExperience, referentDates);
    
    return {
        score: temporalResult.score,
        issues: temporalResult.issues,
        severity: temporalResult.severity,
        details: {
            startDateGap: temporalResult.details.startDateGapMonths,
            endDateGap: temporalResult.details.endDateGapMonths,
            hasTemporalOverlap: temporalResult.details.overlapMonths > 0,
            durationDifference: temporalResult.details.durationGapMonths
        }
    };
}

/**
 * Analyse la fiabilité de tous les référents d'une campagne (CORRIGÉE)
 * Utilise maintenant la nouvelle logique de cohérence temporelle
 */
export function analyzeCampaignDateReliability(
    experiences: CVExperience[],
    referentResponses: Array<{
        referent_id: string;
        referent_name: string;
        experience_id: string;
        collaboration_start_date: string;
        collaboration_end_date: string | null;
    }>
): Array<{
    referent_id: string;
    referent_name: string;
    experience_id: string;
    reliability: DateReliabilityResult;
}> {
    // Utiliser la nouvelle fonction d'analyse temporelle
    const temporalAnalysis = analyzeCampaignTemporalCoherence(experiences, referentResponses);
    
    return temporalAnalysis.map(analysis => ({
        referent_id: analysis.referent_id,
        referent_name: analysis.referent_name,
        experience_id: analysis.experience_id,
        reliability: {
            score: analysis.coherence.score,
            issues: analysis.coherence.issues,
            severity: analysis.coherence.severity,
            details: {
                startDateGap: analysis.coherence.details.startDateGapMonths,
                endDateGap: analysis.coherence.details.endDateGapMonths,
                hasTemporalOverlap: analysis.coherence.details.overlapMonths > 0,
                durationDifference: analysis.coherence.details.durationGapMonths
            }
        }
    }));
}